import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, List as ListIcon, MessageCircle, FolderKanban, MoveRight } from 'lucide-react';
import api from '@/lib/api';
import { formatRp, formatDate, waLink, STAGES } from '@/lib/format';
import { StageBadge, PaymentBadge } from '@/components/StatusBadge';
import { ListSkeleton } from '@/components/Skeletons';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

function ProjectCard({ p, onMove, onOpen, draggable, onDragStart }) {
  return (
    <div data-testid="proyek-kanban-card" draggable={draggable} onDragStart={onDragStart}
      className={cn('rounded-xl border bg-card p-3 shadow-[var(--shadow-sm)]', draggable && 'cursor-grab active:cursor-grabbing')}>
      <button onClick={onOpen} className="w-full text-left">
        <div className="truncate font-semibold text-sm">{p.nama_proyek}</div>
        <div className="truncate text-xs text-muted-foreground">{p.customer_name}</div>
        <div className="mt-1.5 font-display text-sm font-semibold text-primary tabular-nums">{formatRp(p.nilai)}</div>
      </button>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <PaymentBadge status={p.status_pembayaran} />
        {p.target_selesai && <span className="text-[10px] text-muted-foreground">Target {formatDate(p.target_selesai)}</span>}
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="proyek-pindah-tahap-button" className="flex items-center gap-1 rounded-lg border px-2 py-1 text-xs hover:bg-muted"><MoveRight className="h-3.5 w-3.5" /> Pindah</button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuLabel>Pindahkan ke</DropdownMenuLabel>
            {STAGES.map((s) => <DropdownMenuItem key={s} disabled={s === p.stage} onClick={() => onMove(p.id, s)}>{s}</DropdownMenuItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export default function Proyek() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [stageFilter, setStageFilter] = useState('all');
  const [dragId, setDragId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/projects'); setProjects(r.data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const move = async (id, stage) => {
    setProjects((arr) => arr.map((p) => p.id === id ? { ...p, stage } : p));
    try { await api.patch(`/projects/${id}/stage`, { stage }); toast.success(`Dipindah ke ${stage}`); }
    catch { toast.error('Gagal memindahkan'); load(); }
  };

  const filtered = stageFilter === 'all' ? projects : projects.filter((p) => p.stage === stageFilter);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-xl font-bold">Proyek</h1>
          <p className="text-sm text-muted-foreground">Pantau pekerjaan dari survey hingga selesai.</p>
        </div>
        <div className="flex rounded-lg border bg-card p-0.5">
          <button onClick={() => setView('list')} className={cn('flex h-9 w-9 items-center justify-center rounded-md', view === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground')}><ListIcon className="h-4 w-4" /></button>
          <button data-testid="proyek-view-kanban" onClick={() => setView('kanban')} className={cn('flex h-9 w-9 items-center justify-center rounded-md', view === 'kanban' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground')}><LayoutGrid className="h-4 w-4" /></button>
        </div>
      </div>

      {loading ? <ListSkeleton /> : projects.length === 0 ? (
        <EmptyState icon={FolderKanban} title="Belum ada proyek aktif" description="Mulai dari survey atau konversi penawaran menjadi proyek." actionLabel="Buat Penawaran" onAction={() => navigate('/penawaran/baru')} />
      ) : view === 'list' ? (
        <>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Tahap</SelectItem>
              {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {filtered.map((p) => (
              <div key={p.id} className="rounded-xl border bg-card p-4 transition-shadow hover:shadow-[var(--shadow-md)]">
                <div className="flex items-start justify-between gap-2">
                  <button onClick={() => navigate(`/proyek/${p.id}`)} className="min-w-0 text-left">
                    <div className="truncate font-semibold">{p.nama_proyek}</div>
                    <div className="truncate text-xs text-muted-foreground">{p.customer_name}</div>
                  </button>
                  <StageBadge stage={p.stage} testid="proyek-stage-badge" />
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-display font-semibold text-primary tabular-nums">{formatRp(p.nilai)}</span>
                  <PaymentBadge status={p.status_pembayaran} />
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><button data-testid="proyek-pindah-tahap-button" className="flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs hover:bg-muted"><MoveRight className="h-3.5 w-3.5" /> Pindah Tahap</button></DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuLabel>Pindahkan ke</DropdownMenuLabel>
                      {STAGES.map((s) => <DropdownMenuItem key={s} disabled={s === p.stage} onClick={() => move(p.id, s)}>{s}</DropdownMenuItem>)}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/proyek/${p.id}`)} className="text-xs">Detail</Button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div data-testid="proyek-kanban-board" className="no-scrollbar -mx-4 flex gap-3 overflow-x-auto px-4 pb-2">
          {STAGES.map((stage) => {
            const col = projects.filter((p) => p.stage === stage);
            return (
              <div key={stage} data-testid="proyek-kanban-column"
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragId) { move(dragId, stage); setDragId(null); } }}
                className="flex w-64 shrink-0 flex-col rounded-xl bg-muted/40 p-2">
                <div className="mb-2 flex items-center justify-between px-1">
                  <StageBadge stage={stage} />
                  <span className="text-xs font-semibold text-muted-foreground">{col.length}</span>
                </div>
                <div className="flex flex-col gap-2">
                  {col.map((p) => (
                    <ProjectCard key={p.id} p={p} draggable onDragStart={() => setDragId(p.id)} onMove={move} onOpen={() => navigate(`/proyek/${p.id}`)} />
                  ))}
                  {col.length === 0 && <div className="rounded-lg border border-dashed py-6 text-center text-xs text-muted-foreground">Kosong</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
